import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState } from 'react';

/**
 * SignaturePad — a lightweight touch+mouse signature canvas.
 *
 * Usage:
 *   const ref = useRef();
 *   <SignaturePad ref={ref} />
 *   ref.current.toDataURL()   // "data:image/png;base64,..."
 *   ref.current.isEmpty()
 *   ref.current.clear()
 */
const SignaturePad = forwardRef(function SignaturePad(
  { width = 600, height = 160, strokeColor = '#1F2937', strokeWidth = 2.2, onChange, className = '', 'data-testid': testId = 'signature-pad' },
  ref
) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  // Resize canvas for device pixel ratio so the drawing stays crisp on mobile.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    // Paint an explicit white background on the canvas surface so the
    // pad stays legible in dark mode (the pad's parent Card / Dialog
    // may have a near-black surface; without this the dark ink strokes
    // would render onto a transparent canvas and stay invisible until
    // toDataURL() flattens against a white PNG background).
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = strokeWidth;
  }, [strokeColor, strokeWidth]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getPoint(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = getPoint(e);
    const prev = lastPointRef.current;
    ctx.beginPath();
    ctx.moveTo(prev.x, prev.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    if (isEmpty) {
      setIsEmpty(false);
      onChange && onChange(false);
    }
  };

  const end = () => {
    drawingRef.current = false;
    lastPointRef.current = null;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setIsEmpty(true);
    onChange && onChange(true);
  };

  const toDataURL = () => {
    if (isEmpty) return null;
    return canvasRef.current.toDataURL('image/png');
  };

  useImperativeHandle(ref, () => ({
    clear,
    toDataURL,
    isEmpty: () => isEmpty,
  }));

  return (
    <div
      className={`relative rounded-lg border-2 border-dashed border-[#C4BFF0] ${className}`}
      style={{ width: '100%', background: '#ffffff' }}
    >
      <canvas
        ref={canvasRef}
        data-testid={testId}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        style={{ width: '100%', height: `${height}px`, touchAction: 'none', display: 'block', borderRadius: 8, cursor: 'crosshair' }}
      />
      {isEmpty && (
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center text-[#655dc6]/40 text-sm select-none"
          data-testid="signature-placeholder"
        >
          ✍︎
        </div>
      )}
    </div>
  );
});

export default SignaturePad;
